import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Trash2 } from "lucide-react";
import { deleteClientCredentialApiV1ClientCredentialClientSecretIdDelete } from "@/client";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/use-toast";

export function DeleteClientCredentialDialog({ clientSecretId }: { clientSecretId: string }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const queryClient = useQueryClient();

  const { mutateAsync: deleteClientCredential, isPending } = useMutation({
    mutationFn: () => deleteClientCredentialApiV1ClientCredentialClientSecretIdDelete({ clientSecretId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["client-credentials"] });
      toast({
        title: t("Success"),
        description: t("Client credential deleted successfully"),
      });
    },
    onError: () => {
      toast({
        title: t("Error"),
        description: t("Failed to delete client credential. Please try again later."),
        variant: "destructive",
      });
    },
  });

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger asChild className="flex w-full gap-2">
        <Button variant="destructive">
          <Trash2 className="h-4 w-4" />
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{t("Delete Client Credential")}</AlertDialogTitle>
          <AlertDialogDescription>
            {t("Are you sure you want to delete this client credential? This action cannot be undone.")}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>{t("Cancel")}</AlertDialogCancel>
          <Button
            variant="destructive"
            onClick={async () => {
              await deleteClientCredential();
              setOpen(false);
            }}
            disabled={isPending}
          >
            {isPending ? t("Deleting...") : t("Delete")}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
